import React, { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, Trash2, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { calendarAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
import EventCover from './EventCover';
import useInternalCoverBlob from './useInternalCoverBlob';

/**
 * Phase 26.9 — Cover Image manager for the Event drawer.
 *
 * Reuses the existing event_attachments infrastructure (POST/DELETE
 * /events/{id}/attachments) with `is_cover=true`. There is at most
 * ONE cover per event; uploading a new one un-flags the previous one
 * server-side. Removing the cover deletes the attachment entirely.
 *
 * Accepts image/png and image/jpeg. Max size 25 MiB (enforced by the
 * upload endpoint). Recommended aspect 16:9 — noted in the UI but not
 * enforced; the display component crops via object-cover.
 */
export default function CoverImagePanel({ event, attachments, onChanged }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [ver, setVer] = useState(0);

  // Find the current cover attachment, if any.
  const cover = useMemo(
    () => (attachments || []).find((a) => a.is_cover && !a.deleted_at) || null,
    [attachments],
  );

  // Always route the preview through the blob-URL hook so the same
  // codepath works for public + internal events. The hook short-circuits
  // public URLs and returns them directly; internal URLs are fetched
  // with the bearer token attached.
  const relativeCoverUrl = useMemo(() => {
    if (!cover || !event?.event_id) return null;
    if (event?.visibility_scope === 'public' && event?.status === 'published') {
      return `/api/public/calendar/events/${event.event_id}/cover?v=${ver}`;
    }
    // Internal preview — hit the auth'd download endpoint directly so
    // cache-busting via ?v= works even before the alias would resolve.
    return `/api/calendar/events/${event.event_id}/attachments/${cover.attachment_id}/download?v=${ver}`;
  }, [cover, event, ver]);
  const previewUrl = useInternalCoverBlob(relativeCoverUrl);

  const canManage = !!event?.event_id && event?.status !== 'archived';

  const handlePick = () => {
    if (!canManage || uploading) return;
    inputRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      toast.error('Cover image must be PNG or JPEG');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error('Cover image exceeds 25 MB');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    // Cover uses whatever visibility the event has; agency-owned + Internal
    // events get 'internal' classification, public events get 'public'.
    fd.append('classification', event?.visibility_scope === 'public' ? 'public' : 'internal');
    fd.append('is_cover', 'true');
    setUploading(true);
    try {
      await calendarAPI.uploadAttachment(event.event_id, fd);
      toast.success(cover ? 'Cover image replaced' : 'Cover image uploaded');
      setVer((v) => v + 1);
      await onChanged?.();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to upload cover image'));
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!cover || !canManage) return;
    if (!window.confirm('Remove the cover image? This cannot be undone.')) return;
    try {
      await calendarAPI.deleteAttachment(event.event_id, cover.attachment_id);
      toast.success('Cover image removed');
      setVer((v) => v + 1);
      await onChanged?.();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to remove cover image'));
    }
  };

  return (
    <div className="mb-5 pb-5 border-b border-slate-200" data-testid="cover-image-panel">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <ImageIcon size={14} /> Cover image
          </div>
          <p className="text-[11px] text-slate-500">
            Optional. Recommended 16:9, PNG or JPEG, up to 25 MB. Used as the
            hero banner on the public event page and a thumbnail internally.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg"
            onChange={handleFile}
            className="hidden"
            data-testid="cover-image-file-input"
          />
          <Button
            type="button"
            size="sm"
            variant={cover ? 'outline' : 'default'}
            onClick={handlePick}
            disabled={!canManage || uploading}
            data-testid="cover-image-upload-btn"
          >
            <Upload size={12} className="mr-1" />
            {uploading ? 'Uploading…' : cover ? 'Replace' : 'Upload'}
          </Button>
          {cover && canManage && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={handleRemove}
              className="text-red-600 hover:text-red-700"
              data-testid="cover-image-remove-btn"
            >
              <Trash2 size={12} className="mr-1" /> Remove
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-md">
        <EventCover
          coverUrl={previewUrl}
          title={event?.title || 'Event'}
          category={event?.category}
          variant="hero"
          data-testid="cover-image-preview"
        />
      </div>
    </div>
  );
}
